import React, {useEffect, useId, useRef, useState} from "react";
import {LocaleStringPropsV2} from "./interface";
import {useTranslation} from "../../../i18n/I18nProvider";
import {LOADING_MESSAGE, subscribeToKey, unsubscribeFromKey} from "../../../i18n/LiveDictionary";
import {Skeleton} from "antd";
import {localeStringFadeStyle, localeStringInjectKeyFrames} from "../../../i18n/StyleHelper";
import {shallowEqual} from "react-redux";


const LocaleString = (props: LocaleStringPropsV2) => {
    const { value, formatValue, style, className, loadingFallback } = props;
    const { t, tlocale, mhash } = useTranslation();
    const subscriberId = useId();
    const [text, setText] = useState(() => t(value, formatValue));
    const prevValueRef = useRef<string>();

    useEffect(() => {
        localeStringInjectKeyFrames(); // ensure fade keyframes are in DOM
    }, []);

    useEffect(() => {
        const updateText = () => {
            setText(t(value, formatValue));
        };

        if (prevValueRef.current && prevValueRef.current !== value) {
            unsubscribeFromKey(prevValueRef.current, subscriberId);
        }

        subscribeToKey(value, subscriberId, updateText);
        prevValueRef.current = value;

        updateText();

        return () => {
            unsubscribeFromKey(value, subscriberId);
        };
    }, [value, formatValue, tlocale, mhash,t, subscriberId]);

    const isLoading = text === LOADING_MESSAGE;

    return (
        <span
            style={{ ...style, ...(isLoading ? {} : localeStringFadeStyle) }}
            className={className}
            title={text}
            lang={tlocale}
        >
      {isLoading ? (
          loadingFallback ?? (
              <Skeleton
                  title={false}
                  paragraph={{ rows: 1, width: "80%" }}
                  active
                  style={{ display: "inline-block", margin: 0 }}
              />
          )
      ) : (
          text
      )}
    </span>
    );
};

export default React.memo(LocaleString, (prev, next) => {
    return (
        prev.value === next.value &&
        prev.formatValue === next.formatValue &&
        prev.className === next.className &&
        prev.loadingFallback === next.loadingFallback &&
        shallowEqual(prev.style, next.style)
    );
});
